import { Controller } from '@hotwired/stimulus';
import Quill from 'quill';
/*
 * Description
 * -------
 *
 * Handles comment form.
 * Can be used on any element.
 * Can be used multiple times in the document.
 *
 * Targets
 * -------
 *
 * - editor - (required) - comment editor element
 * - body   - (required) - hidden comment body input element
 * - submit - (required) - submit button
 *
 * Values
 * -------
 *
 * - max-length     - (required) - maximum length of the comment
 * - placeholder    - (required) - placeholder text for the comment editor
 * - editorContent  - (optional) - initial content for the comment editor
 *
 * Usage
 * -------
 *
 * <div data-controller="comment-form">
 *   <form data-action="turbo:submit-end->comment-form#reset">
 *     <input data-comment-form-target="editor" />
 *     <input data-comment-form-target="body" type="hidden" />
 *     <button data-comment-form-target="submit">Submit</button>
 *   </form>
 * </div>
 */
export default class default_1 extends Controller {
    static targets = ['editor', 'submit', 'body'];
    static values = {
        maxLength: Number,
        placeholder: String,
        editorContent: String,
    };
    connect() {
        this.quill = new Quill(this.editorTarget, {
            theme: 'snow',
            modules: { toolbar: false },
            formats: ['image', 'alt'],
            placeholder: this.placeholderValue,
        });
        // Set enterkeyhint="enter" on editor's first child (contenteditable element)
        this.editorTarget.firstElementChild?.setAttribute('enterkeyhint', 'enter');
        // Set initial content if it exists
        if (this.editorContentValue) {
            const delta = this.quill.clipboard.convert({ html: this.editorContentValue });
            this.quill.setContents(delta, 'silent');
        }
        this.quill.on('text-change', () => {
            // Update body input with the current content of the editor
            this.bodyTarget.value = this.quill.getSemanticHTML();
            // Show or hide submit button based on the length of the comment
            const length = this.quill.getLength();
            if (length > 1) {
                this.showSubmit();
            }
            else {
                this.hideSubmit();
            }
            // Delete text if it exceeds the maximum length
            if (this.quill.getLength() > this.maxLengthValue) {
                this.quill.deleteText(this.maxLengthValue, this.quill.getLength());
            }
        });
        // Override Enter key binding
        this.quill.keyboard.bindings['Enter'].unshift({
            key: 13,
            handler: () => this.submitTarget.click(),
        });
        // Override Cmd+E to toggle emoji picker
        this.quill.keyboard.addBinding({
            key: 'e',
            shortKey: true,
            handler: () => {
                this.dispatch('toggleEmojiPicker');
                return false;
            },
        });
    }
    showSubmit() {
        this.submitTargets.forEach((submit) => {
            submit.disabled = false;
            submit.classList.remove('comment-form__post-button--hidden');
            submit.classList.add('comment-form__post-button--visible');
        });
    }
    hideSubmit() {
        this.submitTargets.forEach((submit) => {
            submit.disabled = true;
            submit.classList.remove('comment-form__post-button--visible');
            submit.classList.add('comment-form__post-button--hidden');
        });
    }
    handleEmojiSelect(event) {
        const selection = this.quill.getSelection();
        const index = selection?.index || 0;
        if (event.detail.native) {
            this.quill.insertText(index, event.detail.native);
        }
        else {
            this.quill.insertEmbed(index, 'image', event.detail.src);
            // Add alt attribute to emoji
            const delta = this.quill.getContents();
            const addedEmoji = delta.ops.find((op) => {
                if (!(typeof op.insert === 'object'))
                    return false;
                if (!op.insert.image)
                    return false;
                return op.insert.image === event.detail.src;
            });
            if (addedEmoji) {
                addedEmoji.attributes = { alt: event.detail.id };
            }
            this.quill.setContents(delta);
            // Set caret position to right after emoji
            this.quill.setSelection(index + 1, 0);
        }
    }
    reset(event) {
        if (event.detail.success) {
            this.bodyTarget.value = '';
            this.quill.setText('');
            this.hideSubmit();
        }
    }
}
